import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Grid, Paper, Typography, Avatar, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Badge, Skeleton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useAuth } from '../context/AuthContext';
import EditProfileDialog from '../components/EditProfileDialog';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PartnerRequestButton from '../components/PartnerRequestButton';
import PartnerRequests from '../components/PartnerRequests';
import { API_URL } from '../utils/api';

const NavBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(123,97,255,0.08)',
  zIndex: 1000,
  padding: theme.spacing(1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: 'var(--elevation-1)',
}));


const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(3),
  }
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: 'white',
  borderRadius: 12,
  padding: theme.spacing(0.75, 2),
  textTransform: 'none',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  background: 'linear-gradient(90deg, var(--gradient-start), var(--gradient-end))',
  boxShadow: '0 8px 20px rgba(123,97,255,0.12)',
  '&:hover': {
    transform: 'translateY(-3px)',
  },
  transition: 'all 0.18s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 'unset',
    padding: theme.spacing(1),
    '& .button-text': {
      display: 'none',
    }
  }
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.5rem',
  background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  transition: 'transform 0.3s ease',
}));

const PageContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  overflowX: 'hidden',
  maxWidth: '100% !important',
  minHeight: '100vh',
  padding: 0,
  paddingTop: '80px',
  background: 'linear-gradient(135deg, rgba(255,106,171,0.04) 0%, rgba(123,97,255,0.03) 50%, rgba(255,106,171,0.02) 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'url("data:image/svg+xml,%3Csvg width=\'52\' height=\'26\' viewBox=\'0 0 52 26\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ff69b4\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z\' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
    opacity: 0.5,
    zIndex: 0,
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  margin: '0 auto',
  padding: theme.spacing(4, 3),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  }
}));

const ProfileTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  '& .gradient-text': {
  fontSize: '3rem',
  fontWeight: 800,
  background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: '0 2px 10px rgba(0,0,0,0.1)',
    display: 'inline-block',
    letterSpacing: '0.05em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.5rem',
    }
  },
  '& .heart-icon': {
  fontSize: '2.5rem',
  color: 'var(--gradient-start)',
    marginTop: '8px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    animation: 'heartBeat 1.5s ease-in-out infinite',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    }
  }
}));

const ProfilePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  background: 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0.92))',
  backdropFilter: 'blur(10px)',
  boxShadow: 'var(--elevation-2)',
  transition: 'all 0.3s ease-in-out',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
    '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(123,97,255,0.15)',
  },
    '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '5px',
    background: 'linear-gradient(45deg, var(--gradient-start), var(--gradient-end))',
  }
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: 140,
  height: 140,
  border: '4px solid #fff',
  boxShadow: '0 8px 32px rgba(123,97,255,0.12)',
  backgroundColor: theme.palette.primary.light,
  margin: '0 auto',
  transition: 'transform 0.3s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    width: 100,
    height: 100,
    border: '3px solid #fff',
  },
    '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const EditButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  bottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.1)',
  },
  transition: 'transform 0.2s ease-in-out',
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1),
  wordBreak: 'break-word',
  '& svg': {
    marginRight: theme.spacing(1),
    color: 'var(--gradient-start)',
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  '& > *:last-child': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
    '& svg': {
      fontSize: '1.2rem',
      marginRight: theme.spacing(0.5),
    }
  }
}));

const AnniversaryCard = styled(ProfilePaper)(({ theme }) => ({
  textAlign: 'center',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9))',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(4),
    '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,106,171,0.06) 0%, rgba(123,97,255,0.06) 100%)',
    zIndex: 0,
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  }
}));

const ProfileName = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  textAlign: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  }
}));

const LocationText = styled(Typography)(({ theme }) => ({
  '&.location-details': {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(4),
    wordBreak: 'break-word',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
      paddingLeft: theme.spacing(2),
    }
  }
}));

// Helper function to calculate age from birthday
function calculateAge(birthday) {
  if (!birthday) return '';
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(() => ({
    name: user?.name || null,
    avatar: user?.avatar || null,
    location: null,
    locationDetails: null,
    birthday: user?.birthday || null,
    bio: user?.bio || null,
    anniversaryDate: user?.anniversaryDate || null,
  }));

  const [partnerData, setPartnerData] = useState({
    name: null,
    avatar: null,
    location: null,
    locationDetails: null,
    birthday: null,
    bio: null,
  });

  const [daysUntilAnniversary, setDaysUntilAnniversary] = useState('Set your anniversary date');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAnniversaryDialogOpen, setIsAnniversaryDialogOpen] = useState(false);
  const [showPartnerRequests, setShowPartnerRequests] = useState(false);
  const [partnerRequestsCount, setPartnerRequestsCount] = useState(0);

  const getLocationDetails = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
      );
      const details = await response.json();
      return {
        displayName: details.display_name,
        area: details.address.suburb || details.address.town || details.address.city || '',
        city: details.address.city || details.address.town || '',
        state: details.address.state || '',
        country: details.address.country || ''
      };
    } catch (error) {
      console.error('Error fetching location details:', error);
      return null;
    }
  };

  const transformUserData = async (data, skipLocationDetails = true) => {
    console.log('Transforming user data:', data);
    
    // Handle location data
    let locationString = "No Location";
    let locationDetails = null;
    
    if (data.location) {
      if (typeof data.location === 'string') {
        locationString = data.location;
      } else if (typeof data.location === 'object') {
        if (data.location.type === 'Point' && Array.isArray(data.location.coordinates)) {
          locationString = `${data.location.coordinates[1].toFixed(6)}, ${data.location.coordinates[0].toFixed(6)}`;
          // Only fetch location details if explicitly requested (not on initial load)
          if (!skipLocationDetails) {
            locationDetails = await getLocationDetails(
              data.location.coordinates[1],
              data.location.coordinates[0]
            );
          }
        } else {
          console.log('Unexpected location format:', data.location);
        }
      }
    }

    // Combine first and last name
    const fullName = data.firstName && data.lastName ? 
      `${data.firstName} ${data.lastName}` : 
      data.name || "No Name";

    // Format birthday if it exists
    const formattedBirthday = data.birthday ? 
      new Date(data.birthday).toLocaleDateString() : 
      "No Birthday";

    const transformed = {
      name: fullName,
      avatar: data.avatar || null,
      location: locationString,
      locationDetails: locationDetails,
      birthday: formattedBirthday,
      bio: data.bio || "No Bio",
      locationData: data.location,
      anniversaryDate: data.anniversaryDate
    };

    console.log('Transformed data:', transformed);
    return transformed;
  };

  const fetchProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      // Fetch user profile - skip location details initially for speed
      const userResponse = await axios.get(`${API_URL}/api/users/me`);
      console.log('User profile response:', userResponse.data);
      const transformedUserData = await transformUserData(userResponse.data, true); // skipLocationDetails = true
      setUserData(transformedUserData);

      // Check if partner data is already populated in the response
      if (userResponse.data.partner) {
        console.log('Partner data from populated field:', userResponse.data.partner);
        const transformedPartnerData = await transformUserData({
          firstName: userResponse.data.partner.firstName,
          lastName: userResponse.data.partner.lastName,
          avatar: userResponse.data.partner.avatar,
          bio: userResponse.data.partner.bio,
          birthday: userResponse.data.partner.birthday,
          location: userResponse.data.partner.location
        }, true); // skipLocationDetails = true
        setPartnerData(transformedPartnerData);
      }
      setIsLoading(false);

      // Fetch location details in background after UI renders
      setTimeout(async () => {
        const userDataWithLocationDetails = await transformUserData(userResponse.data, false);
        setUserData(userDataWithLocationDetails);
        
        if (userResponse.data.partner) {
          const partnerDataWithLocationDetails = await transformUserData({
            firstName: userResponse.data.partner.firstName,
            lastName: userResponse.data.partner.lastName,
            avatar: userResponse.data.partner.avatar,
            bio: userResponse.data.partner.bio,
            birthday: userResponse.data.partner.birthday,
            location: userResponse.data.partner.location
          }, false);
          setPartnerData(partnerDataWithLocationDetails);
        }
      }, 100); // Small delay to let UI render first

    } catch (error) {
      console.error('Error fetching profiles:', error);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (userData.anniversaryDate) {
      const calculateDays = () => {
        const anniversary = new Date(userData.anniversaryDate);
        const today = new Date();
        const nextAnniversary = new Date(today.getFullYear(), anniversary.getMonth(), anniversary.getDate());
        
        if (today > nextAnniversary) {
          nextAnniversary.setFullYear(today.getFullYear() + 1);
        }
        
        const diffTime = Math.abs(nextAnniversary - today);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays}`;
      };

      setDaysUntilAnniversary(calculateDays());

      const updateCountdown = () => {
        setDaysUntilAnniversary(calculateDays());
      };

      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const msUntilMidnight = tomorrow - now;

      const timeoutId = setTimeout(() => {
        updateCountdown();
        const intervalId = setInterval(updateCountdown, 24 * 60 * 60 * 1000);
        return () => clearInterval(intervalId);
      }, msUntilMidnight);

      return () => clearTimeout(timeoutId);
    }
  }, [userData.anniversaryDate]);

  useEffect(() => {
    const fetchPartnerRequests = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users/partner-requests`);
        setPartnerRequestsCount(response.data.length);
      } catch (error) {
        console.error('Error fetching partner requests:', error);
      }
    };

    fetchPartnerRequests();
  }, []);

  const handleRequestHandled = () => {
    setPartnerRequestsCount(prev => Math.max(0, prev - 1));
    fetchProfiles(); // Refresh profiles after handling request
  };

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleSaveProfile = async (formData) => {
    try {
      // Split the name into firstName and lastName
      const [firstName = '', lastName = ''] = formData.name.split(' ');
      
      const dataToSave = {
        firstName,
        lastName,
        bio: formData.bio,
        location: formData.locationData || formData.location,
        birthday: formData.birthday,
        avatar: formData.avatar,
        // Only include preferences if they exist
        ...(formData.preferences && { preferences: formData.preferences })
      };

      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication token missing. Please try logging in again.');
        return;
      }

      console.log('Sending data to backend:', dataToSave);
      const response = await axios.put(`${API_URL}/api/users/me`, dataToSave, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      console.log('Profile update response:', response.data);
      
      // Update the user data immediately with the response
      const updatedUserData = await transformUserData(response.data);
      setUserData(updatedUserData);
      
      // Refresh both profiles
      await fetchProfiles();
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // Show more detailed error message
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code that falls out of the range of 2xx
        errorMessage = `Server error (${error.response.status}): ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Network error: No response from server. Please check your internet connection.';
      } else {
        // Something happened in setting up the request
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleSaveAnniversary = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/users/anniversary`, { anniversaryDate: userData.anniversaryDate }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsAnniversaryDialogOpen(false);
      fetchProfiles(); // Refetch to update UI for both users
    } catch (error) {
      console.error('Error updating anniversary date:', error);
      alert('Failed to update anniversary date. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading) {
    return (
      <PageContainer disableGutters>
        <ProfileTitle variant="h1">
          <span className="gradient-text">Our Profile</span>
          <FavoriteIcon className="heart-icon" />
        </ProfileTitle>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 1, sm: 2 },
          px: { xs: 1, sm: 2 },
          '& > *': {
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }
        }}>
          {/* Your Profile Skeleton */}
          <ProfilePaper elevation={3}>
            <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
              <Skeleton variant="circular" width={140} height={140} sx={{ mx: 'auto', mb: 2 }} />
            </Box>
            <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 2, height: 32 }} />
            <Stack spacing={2}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="rounded" width="100%" height={60} />
            </Stack>
          </ProfilePaper>

          {/* Anniversary Card Skeleton */}
          <AnniversaryCard>
            <Skeleton variant="circular" width={56} height={56} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" width="80%" sx={{ mx: 'auto', mb: 2, height: 28 }} />
            <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 3, height: 40 }} />
            <Skeleton variant="rounded" width="100%" height={40} />
          </AnniversaryCard>

          {/* Partner Profile Skeleton */}
          <ProfilePaper elevation={3}>
            <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
              <Skeleton variant="circular" width={140} height={140} sx={{ mx: 'auto', mb: 2 }} />
            </Box>
            <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 2, height: 32 }} />
            <Stack spacing={2}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="rounded" width="100%" height={60} />
            </Stack>
          </ProfilePaper>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer disableGutters>
      <ProfileTitle variant="h1">
        <span className="gradient-text">Our Profile</span>
        <FavoriteIcon className="heart-icon" />
      </ProfileTitle>

      {/* Partner Requests Button - Repositioned with new style */}
      <Box sx={{ 
        position: 'absolute', 
        /* Keep button near top on desktop; on small screens place it just under the nav and collapse text */
        top: { xs: 'calc(var(--nav-height) + 8px)', sm: 20, md: 20 }, 
        right: { xs: 12, sm: 20, md: 20 },
        zIndex: 10,
        display: 'flex',
        alignItems: 'center'
      }}>
        <Button
          variant="contained"
          onClick={() => setShowPartnerRequests(true)}
          startIcon={<FavoriteIcon />}
          endIcon={partnerRequestsCount > 0 ? 
            <Badge badgeContent={partnerRequestsCount} color="error" /> : 
            null
          }
          size="small"
          sx={{
            background: 'linear-gradient(45deg, var(--gradient-end) 0%, var(--gradient-start) 100%)',
            color: 'white',
            borderRadius: '18px',
            px: { xs: 1, sm: 2 },
            py: { xs: 0.4, sm: 0.9 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            boxShadow: '0 4px 10px rgba(123,97,255,0.28)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 15px rgba(123,97,255,0.36)',
            },
            transition: 'all 0.22s ease',
            whiteSpace: 'nowrap',
            /* hide long button text on very small screens */
            '& .partner-text': { display: { xs: 'none', sm: 'inline' } },
            '& .MuiButton-startIcon': { mr: { xs: 0.4, sm: 1 } }
          }}
        >
          <span className="partner-text">Partner Requests</span>
          {partnerRequestsCount > 0 && (
            <Typography component="span" sx={{ ml: 1, fontWeight: 'bold', fontSize: 'inherit' }}>
              ({partnerRequestsCount})
            </Typography>
          )}
        </Button>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        gap: { xs: 1, sm: 2 },
        px: { xs: 1, sm: 2 },
        '& > *': {
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }
      }}>
        {/* Your Profile */}
        <ProfilePaper elevation={3}>
          <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
            <LargeAvatar
              src={userData.avatar}
              alt={userData.name}
            >
              {!userData.avatar && userData.name?.charAt(0)}
            </LargeAvatar>
            <EditButton
              onClick={handleEditClick}
              size="large"
            >
              <EditIcon />
            </EditButton>
          </Box>

          <ProfileName variant="h2">
            {userData.name || "Your Name"}
          </ProfileName>

          <Box>
            <InfoItem>
              <LocationOnIcon sx={{ color: 'var(--gradient-start)' }} />
              <Box sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                <LocationText>{userData.location}</LocationText>
                {userData.locationDetails && (
                  <LocationText className="location-details">
                    {userData.locationDetails.area && `${userData.locationDetails.area}, `}
                    {userData.locationDetails.city && `${userData.locationDetails.city}, `}
                    {userData.locationDetails.state && `${userData.locationDetails.state}, `}
                    {userData.locationDetails.country}
                  </LocationText>
                )}
              </Box>
            </InfoItem>
            <InfoItem>
              <CalendarTodayIcon sx={{ color: 'var(--gradient-start)' }} />
              <Typography>{userData.birthday}</Typography>
            </InfoItem>
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                fontStyle: 'italic',
                textAlign: 'center',
                color: 'text.secondary',
                padding: 2,
                borderLeft: '3px solid',
                borderColor: 'var(--gradient-start)',
                backgroundColor: 'rgba(255,106,171,0.04)',
                borderRadius: '0 8px 8px 0',
              }}
            >
              {userData.bio}
            </Typography>
          </Box>
        </ProfilePaper>

        {/* Anniversary Card - Center */}
        <AnniversaryCard>
          <FavoriteIcon 
            sx={{ 
              fontSize: { xs: 48, lg: 56 },
              color: 'var(--gradient-start)',
              marginBottom: 2,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              animation: 'heartBeat 1.5s ease-in-out infinite',
            }} 
          />
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.5rem', lg: '1.75rem' },
              mb: 2,
              background: 'linear-gradient(45deg, var(--gradient-start), var(--gradient-end))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Days Until Our Anniversary
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              fontSize: { xs: '3rem', lg: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              mb: 2
            }}
          >
            {daysUntilAnniversary}
          </Typography>
          <Button
            variant="outlined"
            sx={{
              mt: 'auto',
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              borderColor: 'var(--gradient-start)',
              color: 'var(--gradient-start)',
              '&:hover': {
                borderColor: 'var(--gradient-start)',
                backgroundColor: 'rgba(255,106,171,0.06)',
                transform: 'scale(1.05)',
              }
            }}
            onClick={() => setIsAnniversaryDialogOpen(true)}
          >
            {userData.anniversaryDate ? 'Update Anniversary Date' : 'Set Anniversary Date'}
          </Button>
        </AnniversaryCard>

        {/* Partner's Profile */}
        <ProfilePaper elevation={3}>
          {partnerData ? (
            <>
              <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
                <LargeAvatar
                  src={partnerData.avatar}
                  alt={partnerData.name}
                >
                  {!partnerData.avatar && partnerData.name?.charAt(0)}
                </LargeAvatar>
              </Box>

              <ProfileName variant="h2">
                {partnerData.name || "Partner's Name"}
              </ProfileName>

              <Box>
                <InfoItem>
                  <LocationOnIcon sx={{ color: 'var(--gradient-start)' }} />
                  <Box sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                    <LocationText>{partnerData.location}</LocationText>
                    {partnerData.locationDetails && (
                      <LocationText className="location-details">
                        {partnerData.locationDetails.area && `${partnerData.locationDetails.area}, `}
                        {partnerData.locationDetails.city && `${partnerData.locationDetails.city}, `}
                        {partnerData.locationDetails.state && `${partnerData.locationDetails.state}, `}
                        {partnerData.locationDetails.country}
                      </LocationText>
                    )}
                  </Box>
                </InfoItem>
                <InfoItem>
                  <CalendarTodayIcon sx={{ color: 'var(--gradient-start)' }} />
                  <Typography>{partnerData.birthday}</Typography>
                </InfoItem>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    color: 'text.secondary',
                    padding: 2,
                    borderLeft: '3px solid',
                    borderColor: 'var(--gradient-start)',
                    backgroundColor: 'rgba(255,106,171,0.04)',
                    borderRadius: '0 8px 8px 0',
                  }}
                >
                  {partnerData.bio}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No partner connected yet
              </Typography>
              <Box sx={{ mt: { xs: 2, sm: 0 } }}>
                <PartnerRequestButton
                  userId={user?.id}
                  onRequestSent={() => fetchProfiles()}
                />
              </Box>
            </Box>
          )}
        </ProfilePaper>
      </Box>

      {/* Edit Profile Dialog */}
      <EditProfileDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        userData={userData}
        onSave={handleSaveProfile}
      />

      {/* Anniversary Date Dialog */}
      <Dialog
        open={isAnniversaryDialogOpen}
        onClose={() => setIsAnniversaryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--elevation-2)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <FavoriteIcon sx={{ fontSize: '1.8rem' }} />
          Set Your Special Day
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 3,
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            Choose the date that marks the beginning of your beautiful journey together
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={userData.anniversaryDate}
            onChange={(e) => {
              setUserData(prev => ({ ...prev, anniversaryDate: e.target.value }));
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&:hover fieldset': {
                  borderColor: 'var(--gradient-start)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--gradient-start)',
                  borderWidth: '2px',
                },
              },
              '& input': {
                padding: '16px',
                fontSize: '1.1rem',
                color: '#333',
              }
            }}
          />
        </DialogContent>
        <DialogActions
            sx={{
            p: 3,
            gap: 2,
            justifyContent: 'center',
            borderTop: '1px solid rgba(123,97,255,0.08)',
          }}
        >
          <Button
            onClick={() => setIsAnniversaryDialogOpen(false)}
            sx={{
              borderRadius: '10px',
              px: 4,
              py: 1.2,
              color: '#666',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAnniversary}
            sx={{
              borderRadius: '10px',
              px: 4,
              py: 1.2,
              background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(123,97,255,0.26)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Save Our Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Partner Requests Dialog */}
      <PartnerRequests
        open={showPartnerRequests}
        onClose={() => setShowPartnerRequests(false)}
        onRequestHandled={handleRequestHandled}
      />
    </PageContainer>
  );
};

export default Profile; 